import { FC, SyntheticEvent, useMemo } from 'react';

import useSWR from 'swr';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

import { tournamentsService } from 'v2/api/tournamentsService';
import { SlotModel } from 'v2/types/SlotModel';

interface TournamentSlotPickerProps {
  tournamentId: string;
  gameId: string;
  onChange: (gameId: string) => void;
  error?: string;
  disabled?: boolean;
}

const sortSlots = (slots: SlotModel[]): SlotModel[] =>
  [...slots].sort((first, second) => {
    if (first.providerName === second.providerName) {
      return first.title.localeCompare(second.title);
    }

    return first.providerName.localeCompare(second.providerName);
  });

export const TournamentSlotPicker: FC<TournamentSlotPickerProps> = ({
  tournamentId,
  gameId,
  onChange,
  error,
  disabled,
}) => {
  const { data: slots = [], isLoading } = useSWR(
    tournamentId ? ['getProviderSlotsByTournamentId', tournamentId] : null,
    tournamentsService.getProviderSlotsByTournamentId
  );

  const sortedSlots = useMemo(() => sortSlots(slots), [slots]);

  const selectedSlot = sortedSlots.find((slot) => slot.identifier === gameId) ?? null;

  const handleChange = (_: SyntheticEvent, value: SlotModel | null) => {
    onChange(value?.identifier ?? '');
  };

  return (
    <Autocomplete<SlotModel>
      value={selectedSlot}
      options={sortedSlots}
      loading={isLoading}
      disabled={disabled}
      onChange={handleChange}
      getOptionLabel={(option) => option.title}
      groupBy={(option) => option.providerName}
      isOptionEqualToValue={(option, value) => option.identifier === value.identifier}
      renderInput={(params) => (
        <TextField {...params} label="Game Id" required error={Boolean(error)} helperText={error} />
      )}
    />
  );
};
